import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CalendarDays, CalendarCheck, UserCircle, BellRing, ArrowRight } from 'lucide-react';
import { readCurrentUser } from '../lib/permissions';
import { SUB_STATUS_STYLES, fmtShortDate } from '../components/availability/constants';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtDay = (d) =>
  new Date(d + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

export default function StaffHome() {
  const navigate = useNavigate();
  const user = readCurrentUser();
  const [schedule, setSchedule] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [notLinked, setNotLinked] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/shifts/my`)
      .then((res) => setSchedule(res.data))
      .catch((err) => { if (err.response?.status === 403) setNotLinked(true); });
    axios.get(`${API_URL}/api/availability/my`)
      .then((res) => setAvailability(res.data))
      .catch(() => {});
  }, []);

  const todayStr = new Date().toISOString().slice(0, 10);
  const upcoming = (schedule?.shifts || []).filter((s) => s.date >= todayStr);
  const nextShift = upcoming[0];
  const currentPeriod = availability?.periods?.[0];
  const reminders = currentPeriod?.reminders || [];
  const statusMeta = currentPeriod ? (SUB_STATUS_STYLES[currentPeriod.status] || SUB_STATUS_STYLES.not_started) : null;

  return (
    <div className="space-y-5" data-testid="staff-home-page">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Welcome, {user?.name?.split(' ')[0] || 'there'}</h1>
        <p className="text-sm text-muted-foreground mt-1">Your personal CareDesk home.</p>
      </div>

      {reminders.map((r) => (
        <div key={r.type} className="flex items-center gap-2 rounded-lg border border-[#D99E22]/50 bg-[#FDF0D2]/60 px-4 py-3 text-sm text-[#8A6210]" data-testid={`home-reminder-${r.type}`}>
          <BellRing className="h-4 w-4 shrink-0" />
          {r.message}
        </div>
      ))}

      <div className="grid gap-4 sm:grid-cols-2">
        <Card className="p-5" data-testid="home-next-shift-card">
          <div className="flex items-center gap-2 text-sm font-semibold mb-3">
            <CalendarDays className="h-4 w-4 text-primary" /> Next shift
          </div>
          {notLinked ? (
            <p className="text-sm text-muted-foreground">Your login is not linked to a roster employee yet — ask an admin.</p>
          ) : nextShift ? (
            <>
              <div className="text-lg font-semibold">{fmtDay(nextShift.date)}</div>
              <div className="text-sm text-muted-foreground mt-0.5">
                {nextShift.display_name}{nextShift.start_time ? ` · ${nextShift.start_time} – ${nextShift.end_time}` : ''}
                {nextShift.role === 'secondary' ? ' · Secondary' : ''}
              </div>
              <div className="text-xs text-muted-foreground mt-2">{upcoming.length} upcoming shift{upcoming.length === 1 ? '' : 's'} on your schedule</div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No upcoming shifts scheduled.</p>
          )}
          <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate('/my-schedule')} data-testid="home-goto-schedule">
            My Schedule <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
          </Button>
        </Card>

        <Card className="p-5" data-testid="home-availability-card">
          <div className="flex items-center gap-2 text-sm font-semibold mb-3">
            <CalendarCheck className="h-4 w-4 text-primary" /> Availability
          </div>
          {currentPeriod ? (
            <>
              <div className="text-lg font-semibold">{currentPeriod.period.name}</div>
              <div className="flex items-center gap-2 mt-1.5">
                <Badge variant={statusMeta.variant} data-testid="home-availability-status">{statusMeta.label}</Badge>
                <span className="text-xs text-muted-foreground">Deadline {fmtShortDate(currentPeriod.period.submission_deadline)}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                {currentPeriod.stats.completed_dates}/{currentPeriod.stats.total_dates} dates completed
              </div>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">No open availability period right now.</p>
          )}
          <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate('/my-availability')} data-testid="home-goto-availability">
            My Availability <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
          </Button>
        </Card>
      </div>

      <Card className="p-5" data-testid="home-profile-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <UserCircle className="h-8 w-8 text-muted-foreground" />
            <div>
              <div className="text-sm font-semibold">{user?.name}</div>
              <div className="text-xs text-muted-foreground">{user?.email} · Role: STAFF</div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => navigate('/profile')} data-testid="home-goto-profile">
            Profile &amp; Change Password
          </Button>
        </div>
      </Card>
    </div>
  );
}
